import { useState, useEffect } from 'react';
import { captureException } from '@askporter/exception-logger';
import { Media, AllowedFileTypes, AdminMediaUploadConfig, SimplifiedIAPIClient } from '../../types';
import type { ImageValidation, InvalidFileError } from '../../types';
import { showFileError } from './showFileError';
import { uploadFiles } from './uploadFiles';

interface UseDragAndDropProps {
  imageValidation?: ImageValidation;
  files: Media[];
  maxFiles: number;
  API: () => SimplifiedIAPIClient;
  adminMediaUploadConfig?: AdminMediaUploadConfig;
  onError: (error: string) => void;
  t: (key: string, options?: Record<string, string | number>) => string;
  onChange: (files: Media[]) => void;
}

type UseDragAndDropReturn = {
  isUploading: boolean;
  onValidFilesDrop: (validFiles: File[]) => void;
  onInvalidFilesDrop: (invalidFiles: { file: File; error: InvalidFileError }[]) => void;
  onDelete: (file: Media) => void;
  disableUploader: boolean;
  files: Media[];
};

export const useDragAndDropUpload = ({
  maxFiles,
  API,
  adminMediaUploadConfig,
  files: existingFiles,
  onError,
  t,
  onChange: onChangeCallback,
}: UseDragAndDropProps): UseDragAndDropReturn => {
  const [files, setFiles] = useState<Media[]>(existingFiles);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setFiles(existingFiles);
  }, [JSON.stringify(existingFiles)]);

  const onChange = (newValues: Media[]) => {
    setFiles(newValues);
    onChangeCallback(newValues);
  };

  async function onValidFilesDrop(validFiles: File[]) {
    const uploadingFiles: Media[] = validFiles.map((file: File) => ({
      file: {
        filePath: file.name,
      },
      nameAndType: {
        name: file.name.split('.')[0],
        type: file.type.split('/')[1] as AllowedFileTypes,
      },
      addedAt: new Date().toISOString(),
      size: file.size,
      state: 'loading',
    }));

    let newValues = [];
    if (maxFiles > 1) {
      newValues = [...existingFiles, ...uploadingFiles];
    } else {
      newValues = [...uploadingFiles];
    }
    setFiles(newValues);
    setIsUploading(true);
    const uploadResults = await uploadFiles(validFiles, API, captureException, adminMediaUploadConfig);
    setIsUploading(false);
    const updatedValues: Media[] = newValues.map((value) => {
      const uploadResult = uploadResults.find((result) => {
        return result.name === value.file.filePath;
      });

      if (uploadResult) {
        return {
          ...value,
          state: uploadResult.error ? 'error' : 'success',
          path: adminMediaUploadConfig ? uploadResult.signedUploadPath : undefined, // filePath doesn't exist on /files endpoint
          file: {
            fileRef: uploadResult.urn,
          },
          // path doesn't exist on response, it gets added later by the backend so we need to use the objectURL
          tempPath: uploadResult.objectURL,
          filePath: adminMediaUploadConfig ? uploadResult.filePath : undefined,
        };
      }
      return value;
    });
    onChange(updatedValues);
  }

  function onInvalidFilesDrop(invalidFiles: { file: File; error: InvalidFileError }[]) {
    invalidFiles.forEach((invalidFile) => {
      const errorMessage = showFileError({ t, file: invalidFile.file, error: invalidFile.error });
      onError(errorMessage);
    });
  }

  function onDelete(file: Media) {
    const newValues = files.filter((f) => f.path !== file.path);
    onChange(newValues);
  }
  // For max image of one, only disable if uploading
  // This is due to the fact that when the user drops a new image, we will replace the existing image
  // For multiple images, we want to disable the uploader if the user has reached the max number of images
  // Due to what should happen in that case being too vague
  const disableUploader = (files.length >= maxFiles && maxFiles > 1) || isUploading;

  return {
    isUploading,
    onValidFilesDrop,
    onInvalidFilesDrop,
    onDelete,
    disableUploader,
    files,
  };
};
